import { Drawer } from "@mui/material";
import DigitalTimePicker, {
  decimalToTime,
  timeToDecimal,
} from "components/common/DigitalTimePicker";
import moment from "jalali-moment";
import React, { ReactElement, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { urls } from "../../services/endPoint.ts";
import { api } from "../../services/http.ts";
import { SET_LOADING } from "../../services/redux/reducers/loadingSlice.ts";
import { workerOffs } from "../../services/redux/reducers/userSlice.ts";
import { useAppSelector } from "../../services/redux/store.ts";

const WorkerOffModal = ({ workerOffModal, setWorkerOffModal }) => {
  const userReducer = useAppSelector((state) => state.userReducer);
  const orderReudcer = useAppSelector((state) => state.orderReducer);
  const dispatch = useDispatch();
  const [calTab, setCalTab] = useState(0);
  const [selectedWorkerOffs, setSelectedWorkerOffs] = useState({
    ...userReducer.workerOffs,
  });

  // Initialize with 8:00 - 10:00
  const [fromHour, setFromHour] = useState(8);
  const [fromMinute, setFromMinute] = useState(0);
  const [toHour, setToHour] = useState(10);
  const [toMinute, setToMinute] = useState(0);

  const selectedDate = moment().add(calTab, "d").format("jYYYY/jMM/jDD");
  const currentDayOffs = selectedWorkerOffs[selectedDate] || [];

  // Check for overlaps
  const checkOverlap = (newFromDecimal, newToDecimal, existingOffs) => {
    if (newFromDecimal >= newToDecimal) {
      return "زمان پایان باید بعد از زمان شروع باشد";
    }

    for (const off of existingOffs) {
      // Check if ranges overlap
      if (
        (newFromDecimal >= off.fromTime && newFromDecimal < off.toTime) ||
        (newToDecimal > off.fromTime && newToDecimal <= off.toTime) ||
        (newFromDecimal <= off.fromTime && newToDecimal >= off.toTime)
      ) {
        return "این بازه زمانی با یک تایم مشغولی موجود تداخل دارد";
      }
    }

    return null;
  };

  // Calculate disabled hours for "to" time picker
  const disabledToHours = useMemo(() => {
    const disabled = [];
    const fromDecimal = timeToDecimal(fromHour, fromMinute);

    // Disable hours before or equal to fromHour (considering minutes)
    for (let i = 0; i < 24; i++) {
      const toDecimal = timeToDecimal(i, toMinute);
      if (toDecimal <= fromDecimal) {
        disabled.push(i);
      }
    }
    return disabled;
  }, [fromHour, fromMinute, toMinute]);

  const addWorkerOff = () => {
    const fromDecimal = timeToDecimal(fromHour, fromMinute);
    const toDecimal = timeToDecimal(toHour, toMinute);

    const overlap = checkOverlap(fromDecimal, toDecimal, currentDayOffs);

    if (overlap) {
      toast(overlap, { type: "error", toastId: "worker-off-modal" });
      return;
    }

    setSelectedWorkerOffs((prev) => {
      const cp = { ...prev };
      if (!cp[selectedDate]) {
        cp[selectedDate] = [];
      }
      cp[selectedDate] = [
        ...cp[selectedDate],
        {
          fromTime: fromDecimal,
          toTime: toDecimal,
          isStrict: 0,
        },
      ].sort((a, b) => a.fromTime - b.fromTime);
      return cp;
    });

    toast("تایم مشغولی اضافه شد", {
      type: "success",
      toastId: "worker-off-modal",
    });

    // Reset to default
    setFromHour(8);
    setFromMinute(0);
    setToHour(10);
    setToMinute(0);
  };

  const removeWorkerOff = (index) => {
    setSelectedWorkerOffs((prev) => {
      const cp = { ...prev };
      cp[selectedDate] = cp[selectedDate].filter((_, i) => i !== index);
      if (cp[selectedDate].length === 0) {
        delete cp[selectedDate];
      }
      return cp;
    });
    toast("تایم مشغولی حذف شد", { type: "info", toastId: "worker-off-modal" });
  };

  const submit = async () => {
    dispatch(SET_LOADING(true));
    const res = await api(
      urls.workerOffs,
      { method: "POST", body: { workerOffs: selectedWorkerOffs } },
      true,
    );
    if (res.code == 200) {
      toast("اطلاعات کاربری با موفقیت بروزرسانی شد.", {
        type: "success",
        toastId: "worker-off-modal",
      });
      dispatch(workerOffs());
      setWorkerOffModal(false);
    } else {
      toast(
        "مشکلی پیش آمده، لطفا مجددا امتحان کنید یا با اپراتور تماس بگیرید",
        { type: "error", toastId: "worker-off-modal" },
      );
    }
    dispatch(SET_LOADING(false));
  };

  const calender = () => {
    const tabs: ReactElement[] = [];
    for (let i = 0; i < 37; i++) {
      tabs.push(
        <div
          key={i}
          className={`calTabCell${calTab == i ? " selected" : ""}`}
          onClick={() => setCalTab(i)}
        >
          <span>{moment().add(i, "d").locale("fa").format("DD")}</span>
          <span>
            {i == 0
              ? "امروز"
              : i == 1
                ? "فردا"
                : moment().add(i, "d").locale("fa").format("dddd")}
          </span>
        </div>,
      );
    }

    return (
      <div className="calContainer">
        <div className="calTabs">{tabs}</div>
      </div>
    );
  };

  const formatDecimalTime = (decimal) => {
    const { hour, minute } = decimalToTime(decimal);
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  };

  return (
    <Drawer
      anchor="bottom"
      open={workerOffModal}
      onOpen={() => setWorkerOffModal(true)}
      ModalProps={{}}
    >
      <div className="setWorkerOffModal">
        {calender()}

        <div className="timePickerSection">
          <h3>انتخاب بازه زمانی تایم مشغولی</h3>

          <div className="timePickerRow">
            <div className="timePickerColumn">
              <label>از ساعت:</label>
              <DigitalTimePicker
                hour={fromHour}
                setHour={setFromHour}
                minute={fromMinute}
                setMinute={setFromMinute}
                minHour={8}
                maxHour={20}
              />
            </div>

            <div className="timePickerColumn">
              <label>تا ساعت:</label>
              <DigitalTimePicker
                hour={toHour}
                setHour={setToHour}
                minute={toMinute}
                setMinute={setToMinute}
                minHour={8}
                maxHour={22}
                disabledHours={disabledToHours}
                onDisabledHourSelected={(disabled, closest) => {
                  // toast("ساعت انتخابی نامعتبر است", { type: "warning" });
                }}
              />
            </div>
          </div>

          <button className="addButton" onClick={addWorkerOff}>
            افزودن تایم مشغولی
          </button>
        </div>

        {currentDayOffs.length > 0 && (
          <div className="workerOffsList">
            <h3>
              تایم مشغولی‌های{" "}
              {moment().add(calTab, "d").locale("fa").format("DD MMMM")}
            </h3>
            <div className="workerOffsItems">
              {currentDayOffs.map((off, index) => (
                <div key={index} className="workerOffItem">
                  <span className="timeRange">
                    {formatDecimalTime(off.fromTime)} -{" "}
                    {formatDecimalTime(off.toTime)}
                  </span>
                  {off.orderId && (
                    <span>
                      {orderReudcer.orders.find((e) => e.id == off.orderId)
                        ?.code || off.orderId}
                    </span>
                  )}
                  <button
                    className={`removeButton${off.orderId || off.isStrict ? " disabled" : ""}`}
                    disabled={off.orderId || off.isStrict}
                    onClick={() => removeWorkerOff(index)}
                  >
                    حذف
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="modalButtons">
          <button className="modalConfirmButton" onClick={submit}>
            ثبت
          </button>
          <button
            className="modalCancelButton"
            onClick={() => setWorkerOffModal(false)}
          >
            لغو{" "}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default WorkerOffModal;
